"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';

export type WishItem = {
  id: string;
  name: string;
  price?: number;
  image?: string;
  slug?: string;
  [x: string]: unknown;
};

type WishlistContextShape = {
  items: WishItem[];
  count: number;
  add: (item: WishItem) => void;
  remove: (id: string) => void;
  toggle: (item: WishItem) => void;
  isWished: (id: string) => boolean;
  clear: () => void;
};

export const WishlistContext = createContext<WishlistContextShape | null>(null);

const STORAGE_KEY = 'tvs_wishlist_v1';

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<WishItem[]>([]);

  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (raw) setItems(JSON.parse(raw) as WishItem[]);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore localStorage errors
    }
  }, [items]);

  const add = (item: WishItem) => {
    setItems((prev) => {
      if (prev.find((p) => String(p.id) === String(item.id))) return prev;
      return [...prev, item];
    });
  };

  const remove = (id: string) => setItems((prev) => prev.filter((p) => String(p.id) !== String(id)));

  const toggle = (item: WishItem) => {
    const exists = items.find((p) => String(p.id) === String(item.id));
    if (exists) remove(String(item.id)); else add(item);
  };

  const isWished = (id: string) => items.some((p) => String(p.id) === String(id));

  const clear = () => setItems([]);

  const value: WishlistContextShape = {
    items,
    count: items.length,
    add,
    remove,
    toggle,
    isWished,
    clear,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used inside WishlistProvider');
  return ctx;
};

// A safe accessor that returns null when outside provider (avoids throwing)
export const useWishlistSafe = () => {
  return useContext(WishlistContext) as WishlistContextShape | null;
};

export default WishlistProvider;
